import { useState } from 'react';
import {
  XCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  ArrowRight,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { ValidationResult, ValidationItem } from '../validation/projectCompletionValidation';

type Tab = 'project' | 'service' | 'equipment' | 'ami' | 'results' | 'report';

const TAB_LABELS: Record<string, string> = {
  project: 'Project Info',
  service: 'Service Info',
  equipment: 'New Loads',
  ami: 'AMI Data',
  results: 'Results',
  report: 'Report',
};

interface CompletionValidationModalProps {
  result: ValidationResult;
  /**
   * 'complete' locks the project and refuses while blockers remain.
   * 'report' generates the document either way — an unfinished one comes out
   * stamped DRAFT — INCOMPLETE rather than being withheld, so a preview mid-job
   * stays possible without an incomplete report passing as a finished one.
   */
  mode?: 'complete' | 'report';
  onClose: () => void;
  onComplete: (warningAcknowledged: boolean) => void;
  onNavigate: (tab: Tab) => void;
}

function SeverityIcon({ severity }: { severity: ValidationItem['severity'] }) {
  if (severity === 'BLOCKER')
    return <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />;
  if (severity === 'WARNING')
    return <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />;
  return <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />;
}

function ValidationItemRow({
  item,
  onNavigate,
  onClose,
}: {
  item: ValidationItem;
  onNavigate: (tab: Tab) => void;
  onClose: () => void;
}) {
  const navigateTarget =
    item.fixAction?.type === 'NAVIGATE' && item.fixAction.target
      ? item.fixAction.target
      : null;
  // One line per check. The explanation is a click away, not a paragraph on
  // every row (context/ux.md item 5).
  const [showDetail, setShowDetail] = useState(false);

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <SeverityIcon severity={item.severity} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">
          {item.title}
          {item.message && (
            <button
              type="button"
              onClick={() => setShowDetail(v => !v)}
              aria-expanded={showDetail}
              className="ml-2 text-xs font-normal text-blue-700 underline hover:text-blue-900"
            >
              {showDetail ? 'Hide' : 'Why'}
            </button>
          )}
        </p>
        {showDetail && (
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">{item.message}</p>
        )}
      </div>
      {navigateTarget && (
        <button
          onClick={() => {
            onNavigate(navigateTarget as Tab);
            onClose();
          }}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md whitespace-nowrap flex-shrink-0 transition-colors"
        >
          {TAB_LABELS[navigateTarget] ?? navigateTarget}
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function CollapsibleSection({
  title,
  count,
  colorClass,
  children,
  defaultOpen = true,
}: {
  title: string;
  count: number;
  colorClass: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 ${colorClass} text-left`}
      >
        <span className="text-sm font-semibold">
          {title} ({count})
        </span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && <div className="px-4 bg-white divide-y divide-gray-50">{children}</div>}
    </div>
  );
}

export default function CompletionValidationModal({
  result,
  mode = 'complete',
  onClose,
  onComplete,
  onNavigate,
}: CompletionValidationModalProps) {
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  const { blockers, warnings, advisories, summary } = result;
  const hasBlockers = summary.blockerCount > 0;
  const hasWarnings = summary.warningCount > 0;
  const isReport = mode === 'report';

  // Warnings gate completion, not report generation — a draft is already marked
  // as unfinished, so demanding a disclosure signature to preview one is noise.
  const canComplete = isReport || (!hasBlockers && (!hasWarnings || warningAcknowledged));

  const summaryParts: string[] = [];
  if (summary.blockerCount > 0)
    summaryParts.push(`${summary.blockerCount} blocker${summary.blockerCount !== 1 ? 's' : ''}`);
  if (summary.warningCount > 0)
    summaryParts.push(`${summary.warningCount} warning${summary.warningCount !== 1 ? 's' : ''}`);
  if (summary.advisoryCount > 0)
    summaryParts.push(`${summary.advisoryCount} advisor${summary.advisoryCount !== 1 ? 'ies' : 'y'}`);

  const summaryLine =
    summaryParts.length > 0 ? summaryParts.join(', ') : 'No issues found';

  return (
    <div data-testid="validation-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {isReport ? 'Generate Report' : 'Complete Project'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isReport ? 'Report readiness check' : 'Pre-completion validation'} — {summaryLine}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status banner */}
        <div
          className={`px-5 py-3 flex-shrink-0 ${
            hasBlockers
              ? 'bg-red-50 border-b border-red-100'
              : hasWarnings
              ? 'bg-yellow-50 border-b border-yellow-100'
              : 'bg-green-50 border-b border-green-100'
          }`}
        >
          <div className="flex items-center gap-2">
            {hasBlockers ? (
              <XCircle className="w-5 h-5 text-red-500" />
            ) : hasWarnings ? (
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
            <p
              className={`text-sm font-medium ${
                hasBlockers
                  ? 'text-red-800'
                  : hasWarnings
                  ? 'text-yellow-800'
                  : 'text-green-800'
              }`}
            >
              {hasBlockers
                ? isReport
                  ? 'This report will be stamped DRAFT — INCOMPLETE until the items below are resolved.'
                  : 'Fix the blockers below before completing this project.'
                : hasWarnings
                ? isReport
                  ? 'No blockers. The report will generate without a draft stamp.'
                  : 'No blockers. Review warnings and acknowledge to proceed.'
                : isReport
                ? 'All required checks passed. The report will generate as a final document.'
                : 'All required checks passed. This project is ready to complete.'}
            </p>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div data-testid="validation-blockers">
            <CollapsibleSection
              title="Blockers"
              count={summary.blockerCount}
              colorClass="bg-red-50 text-red-900"
              defaultOpen={true}
            >
              {blockers.map(item => (
                <ValidationItemRow
                  key={item.id}
                  item={item}
                  onNavigate={onNavigate}
                  onClose={onClose}
                />
              ))}
            </CollapsibleSection>
          </div>

          <div data-testid="validation-warnings">
            <CollapsibleSection
              title="Warnings"
              count={summary.warningCount}
              colorClass="bg-yellow-50 text-yellow-900"
              defaultOpen={true}
            >
              {warnings.map(item => (
                <ValidationItemRow
                  key={item.id}
                  item={item}
                  onNavigate={onNavigate}
                  onClose={onClose}
                />
              ))}
            </CollapsibleSection>
          </div>

          <CollapsibleSection
            title="Advisories"
            count={summary.advisoryCount}
            colorClass="bg-blue-50 text-blue-900"
            defaultOpen={false}
          >
            {advisories.map(item => (
              <ValidationItemRow
                key={item.id}
                item={item}
                onNavigate={onNavigate}
                onClose={onClose}
              />
            ))}
          </CollapsibleSection>

          {!hasBlockers && !hasWarnings && summary.advisoryCount === 0 && (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">All checks passed.</p>
              <p className="text-sm text-gray-500 mt-1">
                Click "Complete Project" to lock this project as read-only.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex-shrink-0 space-y-3">
          {/* Warning acknowledgment checkbox */}
          {!isReport && !hasBlockers && hasWarnings && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={warningAcknowledged}
                onChange={e => setWarningAcknowledged(e.target.checked)}
                data-testid="validation-acknowledge"
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 leading-snug">
                I understand the warnings above. These data quality items will be reflected in the
                completed report and I accept responsibility for their disclosure.
              </span>
            </label>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              data-testid="btn-validation-cancel"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>

            {hasBlockers && !isReport ? (
              <button
                disabled
                className="px-4 py-2 text-sm font-medium text-white bg-red-300 rounded-md cursor-not-allowed"
                title="Fix all blockers before completing"
              >
                Fix Blockers to Complete
              </button>
            ) : (
              <button
                onClick={() => onComplete(warningAcknowledged)}
                disabled={!canComplete}
                data-testid={isReport ? 'btn-generate-report-confirm' : 'btn-mark-complete'}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                  !canComplete
                    ? 'bg-gray-300 cursor-not-allowed'
                    : isReport && hasBlockers
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                {isReport
                  ? hasBlockers
                    ? 'Generate Draft Report'
                    : 'Generate Report'
                  : hasWarnings
                  ? 'Complete Anyway'
                  : 'Complete Project'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
